import { useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import {
  Table,
  TableHead,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import { ButtonType } from "@/components/lib/Button";
import { Trash2, Plus } from "lucide-react";
import {
  removeItemVariant,
  setItemVariants,
} from "@/redux/slice/essential-menu-slice/variants-slice";
import FormInputField from "@/components/lib/from-input-field";
import CreateExtraCourseItem from "@/components/dialog/menu-manage/common-dialog/create-extra-course-item";
import CreateCourseItem from "@/components/dialog/menu-manage/common-dialog/create-course-item";
import { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-hot-toast";
import {
  fetchSingleBanquet,
  updateExistingBanquet,
} from "@/redux/slice/menu-manage-slice/special-menu/banquet-slice";
import { Loader2 } from "lucide-react";

function Step2({ form, extraItems, setExtraItems, item_id }) {
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [extraCourseDialgo, setExtraCourseDialog] = useState(false);
  const dispatch = useDispatch();
  const { itemVariants } = useSelector((state) => state.essentialMenu.variants);

  useEffect(() => {
    const fetchSingleItem = async () => {
      try {
        const res = await dispatch(fetchSingleBanquet(item_id));
        if (fetchSingleBanquet.fulfilled.match(res)) {
          const item = res?.payload?.courses;
          dispatch(setItemVariants(item || []));
          setExtraItems(res?.payload?.extra_items || []);
        }
      } catch (error) {
        console.log(error);
      }
    };
    fetchSingleItem();
  }, []);

  const handleItemUpdate = async (payload, actionType = "add") => {
    if (!payload) return;
    setLoading(true);
    let allItem = [...itemVariants];
    if (actionType === "add") {
      allItem = [...itemVariants, payload];
    } else if (actionType === "remove") {
      allItem = itemVariants.filter((item) => item.unique_id !== payload);
    }
    form.setValue("courses", allItem);
    try {
      const res = await dispatch(
        updateExistingBanquet({
          id: item_id,
          data: {
            name: form.watch("name"),
            status: `${
              form.watch("type") === "update" ? form.watch("status") : "draft"
            }`,
            courses: allItem,
          },
        })
      );
      if (updateExistingBanquet.fulfilled.match(res)) {
        // toast.success("Combo updated successfully.");
      } else {
        // toast.error(res?.payload || "Failed to update the combo.");
      }
    } catch (error) {
      toast.error("Failed to update the combo meal.");
      setLoading(false);
    }
    setLoading(false);
  };

  useEffect(() => {
    form.setValue("courses", itemVariants);
  }, [itemVariants]);

  useEffect(() => {
    const mappedItems = itemVariants?.map((item) => ({
      item_id: item.item_id,
      item_type: item.item_type,
      variant_id: item.variant_id,
      file_manager_id: item.file_manager_id,
      quantity: item.quantity,
      extra_charge: item.extra_charge ?? null,
    }));

    form.setValue("extra_items", mappedItems);
  }, [extraItems]);

  // const handleRemove = async (id) => {
  //   await dispatch(removeItemVariant(id));
  //   await handleItemUpdate(id, "remove");
  //   toast.success("Course removed successfully");
  // };

  const handleRemove = async (id) => {
    const courseVariant = itemVariants.find(
      (course) => course.unique_id === id
    );
    if (!courseVariant) return;

    const updatedItemVariants = itemVariants.filter(
      (course) => course.unique_id !== id
    );
    const removedItems = courseVariant?.course_items || [];
    const updatedExtraItems = extraItems.filter(
      (extra) =>
        !removedItems.some(
          (removed) =>
            removed.item_id === extra.item_id &&
            removed.item_type === extra.item_type
        )
    );
    dispatch(setItemVariants(updatedItemVariants));
    setExtraItems(updatedExtraItems);
    form.setValue("courses", updatedItemVariants);
    form.setValue("extra_items", updatedExtraItems);
    await handleItemUpdate(id, "remove");
    toast.success("Course and related extra items removed successfully");
  };

  const handleRemoveItem = (itemId, itemType) => {
    const updated = itemVariants.map((course) => ({
      ...course,
      course_items: course?.course_items.filter(
        (item) => !(item.item_id === itemId && item.item_type === itemType)
      ),
    }));

    const allEmpty = updated.every(
      (course) => course.course_items.length === 0
    );

    setExtraItems((prevItems) =>
      prevItems.filter(
        (item) => !(item.item_id === itemId && item.item_type === itemType)
      )
    );
    dispatch(setItemVariants(allEmpty ? [] : updated));
    toast.success("Course Item removed successfully");
  };

  const handleRemoveExtraItem = (itemId, itemType) => {
    setExtraItems((prevItems) =>
      prevItems.filter(
        (item) => !(item.item_id === itemId && item.item_type === itemType)
      )
    );
    toast.success("Extra charge item removed successfully");
  };

  return (
    <Card className="max-w-screen-md bg-cblack6 mx-auto mt-10 text-cwhite">
      <CardHeader>
        <CardTitle>
          {" "}
          {form.watch("type") == "update"
            ? "Update Banquet Night"
            : "Add New Banquet Night"}
        </CardTitle>
        <CardDescription className="text-cyellow">
          Item name: {form.watch("name") || "No name"}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Items Section */}
        <div className="flex justify-between items-center mb-4">
          <h2 className="">Course List</h2>
          <ButtonType
            onClick={() => setOpenDialog(true)}
            className="bg-cgreen/70 text-cwhite flex flex-shrink-0"
          >
            <span className="flex items-center">
              <Plus className="w-4 h-4 mr-1" size={18} /> Add New Course
            </span>
          </ButtonType>
        </div>

        <div className="flex flex-col gap-5 relative">
          {itemVariants &&
            itemVariants?.length > 0 &&
            itemVariants?.map((variant, val) => (
              <div className="border border-gray-500" key={val}>
                <div className="flex justify-between items-center gap-5 py-3 bg-cblack5 px-2">
                  <div className="flex items-end gap-2">
                    <h2 className="text-lg tracking-tight font-semibold">
                      {variant?.name}
                    </h2>
                    <p className="text-sm ">{variant?.description}</p>
                  </div>
                  <Trash2
                    onClick={() => handleRemove(variant?.unique_id)}
                    className="text-cred cursor-pointer"
                    size={18}
                  />
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Price</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {variant?.course_items &&
                    variant?.course_items?.length > 0 ? (
                      variant?.course_items?.map((item, i) => (
                        <TableRow key={i}>
                          <TableCell>
                            {item?.name ||
                              item?.menu_item?.name ||
                              "Data not available"}
                            {(item?.variant_name || item?.variant?.name) && ` (${item?.variant_name || item?.variant?.name})`}
                          </TableCell>
                          <TableCell>{item.price ?? "0"}</TableCell>

                          <TableCell className="text-right">
                            <span className="flex items-center justify-end gap-3">
                              <Trash2
                                onClick={() =>
                                  handleRemoveItem(
                                    item?.item_id,
                                    item?.item_type
                                  )
                                }
                                className="w-4 h-4 text-cred cursor-pointer"
                              />
                            </span>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center">
                          No data available
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            ))}
          {loading && (
            <div className="absolute inset-0 bg-black/40 flex justify-center items-center z-10">
              <Loader2 className="animate-spin" size={18} />
            </div>
          )}
        </div>

        <div className="mt-6 flex flex-col gap-6">
          <FormInputField
            name="person_limit"
            style="bg-cblack7"
            placeholder={"1"}
            label={"Person limit"}
            form={form}
            type="text"
          />

          {/* Items Section */}
          <div className="flex justify-between items-center">
            <h2 className="">Item with Extra Charge</h2>
            <ButtonType
              onClick={() => setExtraCourseDialog(true)}
              className="bg-cgreen/70 text-cwhite flex flex-shrink-0"
            >
              <span className="flex items-center">
                <Plus className="w-4 h-4 mr-1" size={18} /> Add New Item
              </span>
            </ButtonType>
          </div>
          {extraItems && extraItems.length > 0 && (
            <div className="border border-gray-500">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Regular price</TableHead>
                    <TableHead>Extra price</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {extraItems && extraItems?.length > 0 ? (
                    extraItems?.map((item, i) => (
                      <TableRow key={i}>
                        <TableCell>
                          {item?.name ||
                            item?.menu_item?.name ||
                            "Data not available"}
                        </TableCell>
                        <TableCell>{item.price ?? "0"}</TableCell>
                        <TableCell>{item.extra_charge ?? "0"}</TableCell>
                        <TableCell className="text-right">
                          <span className="flex items-center justify-end gap-3">
                            <Trash2
                              onClick={() =>
                                handleRemoveExtraItem(
                                  item?.item_id,
                                  item?.item_type
                                )
                              }
                              className="w-4 h-4 text-cred cursor-pointer"
                            />
                          </span>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center">
                        No data available
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </CardContent>

      {/* Dialog */}
      <CreateCourseItem
        title={"Create New Course"}
        isOpen={openDialog}
        onClose={() => setOpenDialog(false)}
        onSuccess={(newItem) => handleItemUpdate(newItem, "add")}
      />

      <CreateExtraCourseItem
        title={"Add Extra Item with Charge"}
        extraItems={extraItems}
        setExtraItems={setExtraItems}
        isOpen={extraCourseDialgo}
        onClose={() => setExtraCourseDialog(false)}
      />
    </Card>
  );
}

export default Step2;
